import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { organizationService } from '../services/organizations';
import { Loader2, CheckCircle, XCircle, Building2 } from 'lucide-react';

interface InvitationData {
  organization: {
    id: string;
    name: string;
    slug: string;
  };
  role: string;
  email: string;
}

export const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const token = searchParams.get('token');

  // Validate the invitation token
  useEffect(() => {
    const validateInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const data = await organizationService.validateInvitation(token);
        setInvitationData(data);
        setError(null);

        // If not authenticated, redirect to auth immediately
        if (!isAuthenticated) {
          localStorage.setItem('pendingInvitationReturn', `/accept-invitation?token=${token}`);
          navigate('/auth', {
            state: {
              returnTo: `/accept-invitation?token=${token}`,
              invitationEmail: data.email,
            },
            replace: true,
          });
          return;
        }
      } catch (err: any) {
        console.error('Failed to validate invitation:', err);
        setError(err.message || 'This invitation is invalid or has expired');
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, isAuthenticated, navigate]);

  const handleAcceptInvitation = async () => {
    if (!token || !invitationData) return;

    // Check if logged-in user's email matches the invitation
    if (user?.email !== invitationData.email) {
      toast({
        title: 'Email mismatch',
        description: `This invitation was sent to ${invitationData.email}. Please log in with that email address.`,
        variant: 'destructive',
      });
      return;
    }

    // Accept the invitation
    setAccepting(true);
    try {
      await organizationService.acceptInvitation(token);
      
      toast({
        title: 'Success!',
        description: `You've joined ${invitationData.organization.name} as ${invitationData.role}`,
      });
      
      // Reload to refresh organization context
      window.location.href = '/dashboard';
    } catch (err: any) {
      console.error('Failed to accept invitation:', err);
      toast({
        title: 'Failed to accept invitation',
        description: err.message || 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setAccepting(false);
    }
  };

  const getRoleDescription = (role: string) => {
    const descriptions: Record<string, string> = {
      admin: 'Full access to all features including billing and team management',
      manager: 'Can manage stores, products, and approve jobs',
      operator: 'Can view data and create jobs',
      viewer: 'Read-only access to view data',
    };
    return descriptions[role] || 'Team member';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <XCircle className="h-12 w-12 text-destructive" />
            </div>
            <CardTitle>Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full" 
              onClick={() => navigate('/auth')}
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitationData) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Building2 className="h-12 w-12 text-primary" />
          </div>
          <CardTitle>You're Invited!</CardTitle>
          <CardDescription>
            You've been invited to join an organization on TinyScale Studio
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted rounded-lg p-4 space-y-3">
            <div>
              <p className="text-sm text-muted-foreground">Organization</p>
              <p className="font-semibold">{invitationData.organization.name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Your Role</p>
              <p className="font-semibold">{invitationData.role}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {getRoleDescription(invitationData.role)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Invited Email</p>
              <p className="font-semibold">{invitationData.email}</p>
            </div>
          </div>

          {isAuthenticated && user?.email !== invitationData.email && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-3">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                You're logged in as <strong>{user?.email}</strong>. 
                This invitation is for <strong>{invitationData.email}</strong>.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Button 
              className="w-full" 
              onClick={handleAcceptInvitation}
              disabled={accepting}
            >
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Accept Invitation
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/')}
            >
              Decline
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};